import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, GraduationCap, Users, Trophy, BookOpen, Play, ChevronLeft, ChevronRight } from "lucide-react";

export const SchoolGallery = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const galleryVideos = [
    {
      url: "https://customer-assets.emergentagent.com/job_schoolify-17/artifacts/5gqhi7uw_WhatsApp%20Video%202026-02-23%20at%204.11.10%20AM.mp4",
      title: "School Highlights",
      category: "Video"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_schoolify-17/artifacts/k6l0ynq9_YCPS%2010.mp4",
      title: "Campus Life",
      category: "Video"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b5435c93-033f-4e57-8193-8df477e3abd8/artifacts/tzcmk57i_WhatsApp%20Video%202026-03-12%20at%203.21.47%20AM%20%281%29.mp4",
      title: "STEM Learning",
      category: "Video"
    }
  ];

  const galleryImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/4t43227s_WhatsApp%20Image%202026-01-20%20at%206.48.56%20AM%20-%20Copy.jpeg",
      title: "Library Visit",
      category: "Excursion"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/lkjapsio_WhatsApp%20Image%202026-01-20%20at%206.40.16%20AM%20%281%29.jpeg",
      title: "STEM Education",
      category: "STEM"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_schoolify-17/artifacts/nojw0slz_image.png",
      title: "Football Match & Reading Time",
      category: "Activities"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b5435c93-033f-4e57-8193-8df477e3abd8/artifacts/wmcxmcyk_image.png",
      title: "Mechanical STEM Education",
      category: "Excursion"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b5435c93-033f-4e57-8193-8df477e3abd8/artifacts/hlzg8fjj_image.png",
      title: "Hands-on Equipment Training",
      category: "Excursion"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_b5435c93-033f-4e57-8193-8df477e3abd8/artifacts/53s207gt_image.png",
      title: "STEM Electronics Workshop",
      category: "Excursion"
    },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Auto-advance slideshow every 5 seconds
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const goToGallery = () => navigate("/gallery");

  return (
    <section className="py-16 bg-gray-50" data-testid="school-gallery">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full mb-4">
            <Camera size={18} />
            <span className="font-semibold text-sm">School Life</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#002147] mb-4">
            Moments That Define Us
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore the vibrant life at Young Christian Preparatory School through our gallery of memorable moments.
          </p>
        </div>

        {/* Videos Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Play size={20} className="text-[#FF6B00]" />
            <h3 className="text-xl font-bold text-[#002147]">Featured Videos</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {galleryVideos.map((video, index) => (
              <div 
                key={`video-${index}`}
                className="relative group overflow-hidden rounded-xl shadow-lg bg-black cursor-pointer"
                data-testid={`gallery-video-${index}`}
                onClick={goToGallery}
              >
                <video
                  src={video.url}
                  controls
                  className="w-full h-64 md:h-80 object-contain bg-black"
                  preload="metadata"
                  onClick={(e) => e.stopPropagation()}
                >
                  Your browser does not support the video tag.
                </video>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                  <span className="text-xs bg-[#FF6B00] px-2 py-1 rounded-full text-white">{video.category}</span>
                  <h3 className="font-semibold mt-2 text-white">{video.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Auto-Slideshow Photo Gallery */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Camera size={20} className="text-[#FF6B00]" />
              <h3 className="text-xl font-bold text-[#002147]">Photo Gallery</h3>
            </div>
            <button
              onClick={goToGallery}
              className="text-sm font-semibold text-[#FF6B00] hover:text-[#E65C00] transition-colors"
              data-testid="view-full-gallery-btn"
            >
              View Full Gallery &rarr;
            </button>
          </div>
        </div>

        {/* Slideshow */}
        <div
          className="relative rounded-2xl overflow-hidden shadow-xl cursor-pointer group mb-8"
          style={{ height: "480px" }}
          onClick={goToGallery}
          data-testid="gallery-slideshow"
        >
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
              style={{ opacity: currentSlide === index ? 1 : 0 }}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="text-xs bg-[#FF6B00] px-3 py-1 rounded-full text-white font-medium">{image.category}</span>
                <h3 className="text-2xl font-bold mt-3 text-white">{image.title}</h3>
                <p className="text-white/70 text-sm mt-1">Click to view full gallery</p>
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm hover:bg-white/40 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="slideshow-prev"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm hover:bg-white/40 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="slideshow-next"
          >
            <ChevronRight size={24} />
          </button>

          {/* Dot Indicators */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentSlide(index); }}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  currentSlide === index ? "bg-[#FF6B00] w-7" : "bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Thumbnail Grid - all clickable to gallery */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-12">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className={`relative rounded-lg overflow-hidden cursor-pointer group shadow-md transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                currentSlide === index ? "ring-3 ring-[#FF6B00] ring-offset-2" : ""
              }`}
              onClick={goToGallery}
              data-testid={`gallery-thumb-${index}`}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-20 md:h-24 object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Camera size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: GraduationCap, value: "50+", label: "Graduates Yearly" },
            { icon: Users, value: "50+", label: "Expert Teachers" },
            { icon: Trophy, value: "100+", label: "Awards Won" },
            { icon: BookOpen, value: "15+", label: "Programs" }
          ].map((stat, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow"
              data-testid={`stat-${index}`}
            >
              <stat.icon className="w-10 h-10 mx-auto text-[#FF6B00] mb-3" />
              <div className="text-2xl font-bold text-[#002147]">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SchoolGallery;
